import { useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Play,
  Loader2,
  CheckCircle2,
  ExternalLink,
  ArrowRight,
} from 'lucide-react';
import { useConceptsStore } from '@/stores/concepts';
import type { RawConceptData } from '@/services/api';

type Tab = 'data' | 'relations' | 'resolution' | 'provenance';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'data', label: 'Raw Data' },
  { id: 'relations', label: 'Relations' },
  { id: 'resolution', label: 'Resolution' },
  { id: 'provenance', label: 'Provenance' },
];

export function ConceptDetail() {
  const { selectedConcept, detailLoading, resolving, triggerResolution } =
    useConceptsStore();
  const [tab, setTab] = useState<Tab>('data');

  if (detailLoading) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400">
        <Loader2 size={16} className="animate-spin mr-2" />
        Loading concept...
      </div>
    );
  }

  if (!selectedConcept) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-400">
        Select a concept to view detail
      </div>
    );
  }

  const c = selectedConcept;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-navy-900 truncate">
              {c.name || c.id}
            </h2>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
              <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                {c.entity_type}
              </span>
              <ResolutionBadge status={c.resolution_status} />
              <span>v{c.version}</span>
              <span>
                conf {(c.confidence * 100).toFixed(0)}%
              </span>
              <span>
                complete {(c.completeness * 100).toFixed(0)}%
              </span>
            </div>
          </div>
          {c.resolution_status !== 'resolved' && c.resolution_status !== 'canonical' && (
            <button
              onClick={() => triggerResolution(c.id)}
              disabled={resolving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-teal text-white text-xs font-medium hover:bg-brand-teal-dark disabled:opacity-50"
            >
              {resolving ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Play size={12} />
              )}
              Run Resolution
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 px-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'py-2 px-3 text-xs font-medium transition-colors',
              tab === t.id
                ? 'text-brand-teal border-b-2 border-brand-teal'
                : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {tab === 'data' && <RawDataTab concept={c} />}
        {tab === 'relations' && <RelationsTab concept={c} />}
        {tab === 'resolution' && <ResolutionTab concept={c} />}
        {tab === 'provenance' && <ProvenanceTab concept={c} />}
      </div>
    </div>
  );
}

function ResolutionBadge({ status }: { status?: string | null }) {
  if (!status) return null;
  const color: Record<string, string> = {
    resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    canonical: 'bg-brand-teal/10 text-brand-teal-dark border-brand-teal/30',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    skipped: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border',
        color[status] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      {status}
    </span>
  );
}

function RawDataTab({ concept }: { concept: RawConceptData }) {
  return (
    <pre className="text-xs bg-slate-50 rounded-lg p-3 border border-slate-200 text-slate-700 overflow-auto">
      {JSON.stringify(concept.data, null, 2)}
    </pre>
  );
}

function RelationsTab({ concept }: { concept: RawConceptData }) {
  if (concept.relations.length === 0) {
    return <div className="text-sm text-slate-400">No relations recorded.</div>;
  }
  return (
    <div className="space-y-2">
      {concept.relations.map((r, i) => (
        <div
          key={i}
          className="flex items-center gap-2 text-xs border border-slate-200 rounded-lg px-3 py-2"
        >
          <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
            {r.relationship}
          </span>
          <ArrowRight size={12} className="text-slate-400" />
          <span className="text-slate-700 truncate">{r.entity_id}</span>
          <span className="ml-auto text-slate-500">
            {(r.confidence * 100).toFixed(0)}%
          </span>
        </div>
      ))}
    </div>
  );
}

function ResolutionTab({ concept }: { concept: RawConceptData }) {
  const resolved =
    concept.resolution_status === 'resolved' ||
    concept.resolution_status === 'canonical';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        {resolved ? (
          <CheckCircle2 size={14} className="text-emerald-500" />
        ) : (
          <Loader2 size={14} className="text-amber-500" />
        )}
        <span className="text-slate-700">
          Status: <strong>{concept.resolution_status ?? 'pending'}</strong>
        </span>
      </div>
      {concept.canonical_entity_id && (
        <div className="space-y-1">
          <div className="text-xs text-slate-500 uppercase font-medium">
            Canonical Entity
          </div>
          <Link
            to={`/knowledge/estate/entities/${concept.canonical_entity_id}`}
            className="flex items-center gap-2 text-sm text-brand-teal hover:text-brand-teal-dark"
          >
            <code className="text-xs">{concept.canonical_entity_id}</code>
            <ExternalLink size={12} />
          </Link>
        </div>
      )}
      {concept.resolution_cluster_id && (
        <div className="space-y-1">
          <div className="text-xs text-slate-500 uppercase font-medium">Cluster</div>
          <code className="text-xs text-slate-700">
            {concept.resolution_cluster_id}
          </code>
        </div>
      )}
      {concept.embedding_model && (
        <div className="space-y-1">
          <div className="text-xs text-slate-500 uppercase font-medium">Embedding</div>
          <div className="text-xs text-slate-700">
            {concept.embedding_model}
            {concept.embedding_dimension ? ` · ${concept.embedding_dimension}d` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

function ProvenanceTab({ concept }: { concept: RawConceptData }) {
  return (
    <div className="space-y-4">
      {concept.last_research_id && (
        <div className="space-y-1">
          <div className="text-xs text-slate-500 uppercase font-medium">
            Research Run
          </div>
          <Link
            to={`/knowledge/estate/runs/${concept.last_research_id}`}
            className="flex items-center gap-2 text-sm text-brand-teal hover:text-brand-teal-dark"
          >
            <code className="text-xs">{concept.last_research_id}</code>
            <ExternalLink size={12} />
          </Link>
        </div>
      )}
      <div className="space-y-1">
        <div className="text-xs text-slate-500 uppercase font-medium">Created</div>
        <div className="text-xs text-slate-700">{concept.created_at}</div>
      </div>
      <div className="space-y-1">
        <div className="text-xs text-slate-500 uppercase font-medium">Updated</div>
        <div className="text-xs text-slate-700">{concept.updated_at}</div>
      </div>
      {concept.tags && concept.tags.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-slate-500 uppercase font-medium">Tags</div>
          <div className="flex flex-wrap gap-1">
            {concept.tags.map((tag) => (
              <span
                key={tag}
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
      {concept.sources && concept.sources.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-slate-500 uppercase font-medium">Sources</div>
          <div className="space-y-1.5">
            {concept.sources.map((s, i) => (
              <div key={i} className="text-xs">
                <span className="text-slate-700 font-medium">{s.source_type}</span>
                {s.source_url && (
                  <>
                    {' · '}
                    <a
                      href={s.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-teal hover:text-brand-teal-dark"
                    >
                      {s.source_url}
                    </a>
                  </>
                )}
                {s.source_query && (
                  <div className="text-slate-500 italic mt-0.5">{s.source_query}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {concept.metadata && Object.keys(concept.metadata).length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-slate-500 uppercase font-medium">Metadata</div>
          <pre className="text-xs bg-slate-50 rounded-lg p-3 border border-slate-200 text-slate-700 overflow-auto">
            {JSON.stringify(concept.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
